import { compactKey } from '@/utils/tokenPatterns'

export type SubjectLink = { id: string; name: string; code?: string | null }
export type AliasLink = { alias_name: string; canonical_subject_id: string; is_confirmed?: boolean }

export function buildSubjectIdIndex(subjects: SubjectLink[], aliases: AliasLink[]): Map<string, string | null> {
  const map = new Map<string, string | null>()
  const add = (label: string, id: string) => {
    const raw = label.trim()
    if (!raw || !id) return
    for (const key of [raw.toLowerCase(), compactKey(raw)]) {
      if (!key) continue
      const prev = map.get(key)
      if (prev === undefined) map.set(key, id)
      else if (prev !== id) map.set(key, null)
    }
  }
  for (const subject of subjects) {
    add(subject.name, subject.id)
    if (subject.code) add(subject.code, subject.id)
  }
  for (const alias of aliases) {
    if (alias.is_confirmed === false) continue
    add(alias.alias_name, alias.canonical_subject_id)
  }
  return map
}

export function lookupSubjectId(token: string, index: Map<string, string | null> | undefined): string | null {
  if (!token || !index) return null
  const lower = token.trim().toLowerCase()
  const compact = compactKey(token)
  const hit = index.get(lower) ?? (compact ? index.get(compact) : undefined)
  return hit || null
}

export function enrichSavedSubjects<T extends { field?: string; confirmed?: boolean; canonical_subject_id?: string | null }>(
  saved: Record<string, T>,
  index: Map<string, string | null> | undefined,
): Record<string, T> {
  if (!index) return saved
  const next: Record<string, T> = { ...saved }
  for (const [key, status] of Object.entries(saved)) {
    if (status.field && status.field !== 'subject') continue
    const id = status.canonical_subject_id || lookupSubjectId(key, index)
    if (!id) continue
    next[key] = { ...status, field: status.field || 'subject', confirmed: status.confirmed ?? true, canonical_subject_id: id }
  }
  for (const [key, id] of index) {
    if (!id || next[key]) continue
    next[key] = { field: 'subject', confirmed: true, canonical_subject_id: id } as T
  }
  return next
}
